import CircleIcon from "@mui/icons-material/Circle"
import PauseIcon from "@mui/icons-material/Pause"
import PlayArrowIcon from "@mui/icons-material/PlayArrow"
import StopIcon from "@mui/icons-material/Stop"
import WarningIcon from "@mui/icons-material/Warning"
import { Box, CardActions, Container, Grid2, Typography } from "@mui/material"
import { AddExaminatorDialog } from "components/AddExaminatorDialog/AddExamintatorDialog"
import { StyledButton } from "components/Button/StyledButton"
import { CollapsibleCard } from "components/CollapsiblCard/CollapsibleCard"
import { ContactClientDialog } from "components/ContactClient/ContactClientDialog"
import { ExaminationInfo } from "components/ExaminationInfo/ExaminationInfo"
import { TaskContent } from "components/TaskContent/TaskContent"
import { useStoreActions, useStoreState } from "hooks/storeHooks"
import { enqueueSnackbar } from "notistack"
import { useEffect, useRef, useState } from "react"
import { useParams } from "react-router"
import { allExaminations } from "utils/allExaminations"
import { calculateTotalTimeInSeconds, formatTime } from "utils/dateTimeUtils"
import { getColorForStatus } from "utils/status"
import { Examination, Status, Task, TaskStep, TestData, User } from "../../graphql/types"

export const TaskPage = () => {
  const { projectID, examinationID } = useParams()
  const [examination, setExamination] = useState<Examination | null>(null)
  const [openCard, setOpenCard] = useState<string | null>(null)
  const [openContactClientDialog, setOpenContactClientDialog] = useState(false)
  const [openAddExaminatorDialog, setOpenAddExaminatorDialog] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Timer state management
  const [runningTimers, setRunningTimers] = useState<
    Record<string, { startTime: Date; elapsedTime: number }>
  >({})
  const [currentSessionTimes, setCurrentSessionTimes] = useState<Record<string, number>>({})

  // Use refs to access current values in cleanup
  const runningTimersRef = useRef<Record<string, { startTime: Date; elapsedTime: number }>>({})
  const examinationRef = useRef<Examination | null>(null)

  const projectsState = useStoreState((state) => state.projects)
  const { fetchProjects } = useStoreActions((actions) => actions)
  const updateExamination = useStoreActions((state) => state.updateExamination)
  const updateTaskWorkingTime = useStoreActions((state) => state.updateTaskWorkingTime)

  // Fetch projects if not already loaded
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true)
        setError(null)
        await fetchProjects({ fresh: true })
      } catch (err) {
        console.error("Error loading projects:", err)
        setError("Fehler beim Laden der Projektdaten")
        enqueueSnackbar("Fehler beim Laden der Projektdaten", { variant: "error" })
      } finally {
        setLoading(false)
      }
    }

    if (projectsState.length === 0) {
      loadData()
    } else {
      setLoading(false)
    }
  }, [fetchProjects, projectsState.length])

  useEffect(() => {
    if (!loading && projectsState.length > 0) {
      const project = projectsState.find((project) => projectID === project?.id)
      const exam = project
        ? allExaminations(project).find((examination) => examination.id === examinationID)
        : undefined
      if (exam) {
        // Ensure tasks is always an array
        const examinationWithTasks = {
          ...exam,
          tasks: exam.tasks || [],
        }
        setExamination(examinationWithTasks)
        examinationRef.current = examinationWithTasks
      } else {
        setError("Untersuchung nicht gefunden")
      }
    }
  }, [examinationID, projectID, projectsState, loading])

  // Update refs when state changes
  useEffect(() => {
    runningTimersRef.current = runningTimers
  }, [runningTimers])

  // Timer logic - update all running timers every second
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSessionTimes((prev) => {
        const updated = { ...prev }
        Object.keys(runningTimersRef.current).forEach((taskId) => {
          if (runningTimersRef.current[taskId]) {
            const now = new Date()
            const sessionTime = Math.floor(
              (now.getTime() - runningTimersRef.current[taskId].startTime.getTime()) / 1000
            )
            updated[taskId] = sessionTime
          }
        })
        return updated
      })
    }, 1000)

    return () => clearInterval(interval)
  }, [])

  // Stop all running timers when component unmounts (user leaves page)
  useEffect(() => {
    return () => {
      // Stop all running timers when leaving the page
      // Use refs to get the current state values
      Object.keys(runningTimersRef.current).forEach((taskId) => {
        if (runningTimersRef.current[taskId]) {
          const endTime = new Date()
          const sessionDuration = Math.floor(
            (endTime.getTime() - runningTimersRef.current[taskId].startTime.getTime()) / 1000
          )

          // Find the task and update its working time
          const task = examinationRef.current?.tasks?.find((t) => t.id === taskId)
          if (task && examinationID) {
            // Update the task working time in the store
            updateTaskWorkingTime({
              taskId,
              examinationId: examinationID,
              workingTime: task.workingTime + sessionDuration,
            })
          }
        }
      })
      // Clear the active timer from sessionStorage
      sessionStorage.removeItem("activeTimer")
    }
    // Only run cleanup on unmount - empty dependency array
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onToggle = (examinationID: string, taskID: string) => {
    const cardId = `${projectID}:${examinationID}:${taskID}`
    const isCurrentlyOpen = openCard === cardId

    // Find the task to check its status
    const task = examination?.tasks?.find((t) => t.id === taskID)

    // Don't allow opening/timing for completed tasks
    if (task?.status === Status.DONE) {
      // Still allow toggling the card open/closed for viewing, but don't start timer
      if (isCurrentlyOpen) {
        setOpenCard(null)
      } else {
        // Close any other open card first
        if (openCard) {
          const currentTaskId = openCard.split(":")[2]
          const currentTask = examination?.tasks?.find((t) => t.id === currentTaskId)
          if (currentTask?.status !== Status.DONE) {
            stopTimer(currentTaskId)
          }
        }
        setOpenCard(cardId)
      }
      return
    }

    if (isCurrentlyOpen) {
      // Card is closing - stop timer and update task working time
      stopTimer(taskID)
      setOpenCard(null)
    } else {
      // Card is opening - stop any other running timer and start this one
      if (openCard) {
        // Stop the currently running timer (if it's not a completed task)
        const currentTaskId = openCard.split(":")[2]
        const currentTask = examination?.tasks?.find((t) => t.id === currentTaskId)
        if (currentTask?.status !== Status.DONE) {
          stopTimer(currentTaskId)
        }
      }

      // Automatically update examination status from OPEN to IN_PROGRESS when work begins
      if (examination && examination.status === Status.OPEN) {
        // Auto-updating examination status from OPEN to IN_PROGRESS
        const updatedExamination = {
          ...examination,
          status: Status.IN_PROGRESS,
          // Set startTimestamp when work actually begins
          startTimestamp: new Date().toISOString(),
        }
        updateExamination(updatedExamination)
        setExamination(updatedExamination)
        examinationRef.current = updatedExamination
      }

      // Start timer for this task
      startTimer(taskID)
      setOpenCard(cardId)
    }
  }

  const startTimer = (taskId: string) => {
    const now = new Date()

    setRunningTimers((prev) => ({
      ...prev,
      [taskId]: {
        startTime: now,
        elapsedTime: 0,
      },
    }))

    setCurrentSessionTimes((prev) => ({
      ...prev,
      [taskId]: 0,
    }))

    // Store active timer data for Header component
    sessionStorage.setItem(
      "activeTimer",
      JSON.stringify({
        taskId,
        startTime: now.toISOString(),
      })
    )
  }

  const stopTimer = (taskId: string) => {
    const timerData = runningTimersRef.current[taskId]
    const exam = examinationRef.current
    if (!timerData || !exam) {
      return
    }

    const endTime = new Date()
    const sessionDuration = Math.floor((endTime.getTime() - timerData.startTime.getTime()) / 1000)

    // Find the task and update its working time
    const task = exam.tasks?.find((t) => t.id === taskId)
    if (task) {
      const updatedTask = {
        ...task,
        workingTime: task.workingTime + sessionDuration,
      }

      // Update the examination in the store
      const updatedTasks = exam.tasks?.map((t) => (t.id === taskId ? updatedTask : t)) || []

      const updatedExamination = {
        ...exam,
        tasks: updatedTasks,
      }

      // Update local state immediately and persist to the backend via the store
      updateTaskWorkingTime({
        taskId,
        examinationId: examinationID!,
        workingTime: updatedTask.workingTime,
      })
      setExamination(updatedExamination)
      examinationRef.current = updatedExamination
    }

    // Remove timer from running timers
    setRunningTimers((prev) => {
      const updated = { ...prev }
      delete updated[taskId]
      return updated
    })

    // Remove from current session times
    setCurrentSessionTimes((prev) => {
      const updated = { ...prev }
      delete updated[taskId]
      return updated
    })

    // Clear active timer data from sessionStorage
    sessionStorage.removeItem("activeTimer")
  }

  const handleClick = (action: string, task: Task) => {
    switch (action) {
      case "Zuweisen":
        setOpenAddExaminatorDialog(true)
        break
      case "Kunde":
        setOpenContactClientDialog(true)
        break
      case "Abschließen":
        handleTaskDone(task, Status.DONE)
        break
      case "Aktivieren":
        handleTaskDone(task, Status.OPEN)
        break
    }
  }

  const handleTaskDone = (task: Task, status: Status) => {
    if (examination && examination.tasks && projectID) {
      // If marking task as DONE and it's currently open, stop timer and close card
      if (status === Status.DONE) {
        const taskCardId = `${projectID}:${examinationID}:${task.id}`
        if (openCard === taskCardId) {
          stopTimer(task.id)
          setOpenCard(null)
        }
      }

      const updatedTasks = examination.tasks.map((item) => {
        if (item.id === task.id) {
          return {
            ...item,
            status: status,
          }
        }
        return item
      })
      const updatedExamination = {
        ...examination,
        tasks: updatedTasks || [],
      }
      setExamination(updatedExamination)
      examinationRef.current = updatedExamination
      updateExamination(updatedExamination)
    }
  }

  const handleChangeExaminator = (task: Task, user: User) => {
    if (examination && examination.tasks && projectID) {
      const updatedTasks = examination.tasks.map((item) => {
        if (item.id === task.id) {
          return {
            ...item,
            examinator: user,
          }
        }
        return item
      })
      const updatedExamination = {
        ...examination,
        tasks: updatedTasks || [],
      }
      setExamination(updatedExamination)
      examinationRef.current = updatedExamination
      updateExamination(updatedExamination)
    }
    setOpenAddExaminatorDialog(false)
  }

  const cardActions = (task: Task) => {
    return (
      <CardActions sx={{ width: "100%" }}>
        {task.status !== Status.DONE && (
          <>
            {["Zuweisen", "Kunde", "Abschließen"].map((label) => (
              <StyledButton
                key={label}
                disabled={task.status === Status.DONE}
                size="small"
                fullWidth
                onClick={(e) => {
                  e.stopPropagation() // Prevent event bubbling to the collapsible card
                  handleClick((e.currentTarget as HTMLButtonElement).textContent || "", task)
                }}
              >
                {label}
              </StyledButton>
            ))}
          </>
        )}
        {task.status === Status.DONE && (
          <StyledButton
            buttonType="custom"
            size="small"
            fullWidth
            onClick={(e) =>
              handleClick((e.currentTarget as HTMLButtonElement).textContent || "", task)
            }
          >
            Aktivieren
          </StyledButton>
        )}
      </CardActions>
    )
  }

  const workingTime = (task: Task) => {
    let returnValue = 0
    if (task.taskSteps.includes(TaskStep.HELPER)) {
      returnValue = calculateTotalTimeInSeconds(
        task.testDatas.filter((testData): testData is TestData => testData !== null)
      )
    }

    // Add current session time if this task's timer is running
    const currentSessionTime = currentSessionTimes[task.id] || 0
    return returnValue + task.workingTime + currentSessionTime
  }

  const cardTitle = (taskID: string) => {
    if (examination) {
      const task = examination.tasks.find((task) => task.id === taskID)
      if (task) {
        return (
          <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              <Typography variant="body1">{task.name}</Typography>
              {task.examinator && (
                <Typography variant="body2" color="text.secondary">
                  Prüfer:{" "}
                  {task.examinator.displayName || task.examinator.email || task.examinator.id}
                </Typography>
              )}
            </Box>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              {workingTime(task) > 0 && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  {task.status === Status.DONE ? (
                    <StopIcon sx={{ fontSize: 16 }} />
                  ) : runningTimers[taskID] ? (
                    <PlayArrowIcon sx={{ fontSize: 16, color: "success.main" }} />
                  ) : (
                    <PauseIcon sx={{ fontSize: 16 }} />
                  )}
                  {task.estimatedTime > 0 ? (
                    <Typography variant="caption">
                      {formatTime(workingTime(task)) + "/" + formatTime(task.estimatedTime)}
                    </Typography>
                  ) : (
                    <Typography variant="caption">{formatTime(workingTime(task))}</Typography>
                  )}
                  {runningTimers[taskID] && (
                    <Typography
                      variant="caption"
                      sx={{ color: "success.main", fontWeight: "bold" }}
                    >
                      (+{formatTime(currentSessionTimes[taskID] || 0)})
                    </Typography>
                  )}

                  {workingTime(task) > task.estimatedTime && task.estimatedTime > 0 && (
                    <WarningIcon sx={{ fontSize: 16 }} color="warning" />
                  )}
                </Box>
              )}

              <CircleIcon color={getColorForStatus(task.status)} sx={{ fontSize: 16 }} />
            </Box>
          </Box>
        )
      }
    }
    return null
  }

  return (
    <Container maxWidth="md" sx={{ p: 0 }}>
      {loading && (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography variant="body2" color="text.secondary">
            Loading projects and examination data...
          </Typography>
        </Box>
      )}

      {error && (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        </Box>
      )}

      {!loading && !error && !examination && (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography variant="body2" color="text.secondary">
            No examination found for this ID.
          </Typography>
        </Box>
      )}

      {!loading && !error && examination && !examination.tasks && (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography variant="body2" color="text.secondary">
            No tasks found for this examination.
          </Typography>
        </Box>
      )}

      {!loading && !error && examination && examination.tasks && (
        <>
          {/* Show examination info with operating modes */}

          <ExaminationInfo examination={examination} compact={true} />

          <Grid2 container spacing={1}>
            {(() => {
              const regularTasks = examination.tasks.filter(
                (item) =>
                  !item.taskSteps.includes(TaskStep.ENGINEERING) &&
                  !item.taskSteps.includes(TaskStep.WAITING_TIME)
              )

              return regularTasks.map((item) => (
                <Grid2 size={12} key={item.id}>
                  <CollapsibleCard
                    open={openCard === `${projectID}:${examinationID}:${item.id}`}
                    title={cardTitle(item.id)}
                    onToggle={() => onToggle(examination.id, item.id)}
                    cardActions={cardActions(item)}
                  >
                    <TaskContent task={item} />
                  </CollapsibleCard>
                </Grid2>
              ))
            })()}
          </Grid2>
        </>
      )}
      {!loading && !error && examination && examination.tasks && (
        <Grid2 container spacing={1} sx={{ mt: 5 }}>
          {examination.tasks.map((item) => {
            if (
              item.taskSteps.includes(TaskStep.ENGINEERING) ||
              item.taskSteps.includes(TaskStep.WAITING_TIME)
            ) {
              return (
                <Grid2 size={12} key={item.id}>
                  <CollapsibleCard
                    open={openCard === `${projectID}:${examinationID}:${item.id}`}
                    title={cardTitle(item.id)}
                    onToggle={() => onToggle(examination.id, item.id)}
                    cardActions={cardActions(item)}
                  >
                    <TaskContent task={item} />
                  </CollapsibleCard>
                </Grid2>
              )
            }
            return null
          })}
        </Grid2>
      )}

      <ContactClientDialog
        open={openContactClientDialog}
        onClose={() => setOpenContactClientDialog(false)}
      />

      {!loading && !error && examination && examination.tasks && (
        <AddExaminatorDialog
          open={openAddExaminatorDialog}
          onClose={() => setOpenAddExaminatorDialog(false)}
          task={examination.tasks.find(
            (task) => openCard === `${projectID}:${examinationID}:${task.id}`
          )}
          onChangeExaminator={handleChangeExaminator}
        />
      )}
    </Container>
  )
}
